import { useCallback, useEffect, useState } from "react";
import { apiGet, type PageData } from "../../api";
import type { AppRoute } from "../../routes";
import { useSessionContext } from "../../sessionContext";

type AuditRow = {
  id?: number;
  at?: string;
  username?: string;
  action?: string;
  ok?: boolean;
  detail?: string;
  client_ip?: string;
};

type Cursor = { at: string; id: number };

const ACTION_LABEL: Record<string, string> = {
  login_ok: "Signed in",
  login_fail: "Wrong password",
  login_locked: "Sign-in paused",
  otp_fail: "Wrong authenticator code",
  otp_locked: "Authenticator paused",
  logout: "Signed out",
  user_create: "User created",
  user_update: "User updated",
  password_change: "Password changed",
  user_delete: "User deleted",
  totp_setup: "Authenticator setup started",
  totp_on: "Authenticator turned on",
  totp_off: "Authenticator turned off",
  gateway_add: "Gateway added",
  device_add: "Device added",
  api_key_rotate: "API key rotated",
  api_token: "API token issued",
  inventory_site_edit: "Site edited",
  inventory_site_remove: "Site removed",
  inventory_site_update: "Record updated",
};

function whenLabel(raw: string, timeZone: string): string {
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return raw || "—";
  if (!timeZone) return parsed.toLocaleString();
  return new Intl.DateTimeFormat(undefined, {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).format(parsed);
}

function csvCell(value: string): string {
  const text = value.replaceAll("\r", " ").replaceAll("\n", " ");
  if (/[",]/.test(text)) return `"${text.replaceAll('"', '""')}"`;
  return text;
}

function rowsToCsv(items: AuditRow[], timeZone: string): string {
  const lines = ["When,User,Event,Detail,Address"];
  for (const row of items) {
    lines.push(
      [
        whenLabel(String(row.at || ""), timeZone),
        row.username || "",
        ACTION_LABEL[String(row.action || "")] || row.action || "",
        row.detail || "",
        row.client_ip || ""
      ]
        .map((value) => csvCell(String(value)))
        .join(",")
    );
  }
  return lines.join("\r\n");
}

export function AccountActivity() {
  const { session } = useSessionContext();
  const timeZone = String(session?.system_timezone || "Asia/Yangon");
  const [rows, setRows] = useState<AuditRow[]>([]);
  const [keepDays, setKeepDays] = useState(7);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState("");
  const [username, setUsername] = useState("");
  const [action, setAction] = useState("");
  const [failedOnly, setFailedOnly] = useState(false);
  const [cursor, setCursor] = useState<Cursor | null>(null);
  const [back, setBack] = useState<Array<Cursor | null>>([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const filterQuery = useCallback((next: Cursor | null) => {
    const params = new URLSearchParams();
    if (username.trim()) params.set("username", username.trim());
    if (action) params.set("action", action);
    if (failedOnly) params.set("failed", "1");
    if (next) {
      params.set("before_at", next.at);
      params.set("before_id", String(next.id));
    }
    return params.toString();
  }, [action, failedOnly, username]);

  const load = useCallback((next: Cursor | null, history: Array<Cursor | null>) => {
    const query = filterQuery(next);
    setLoading(true);
    setError("");
    apiGet<{ keep_days?: number; events?: AuditRow[]; has_more?: boolean }>(
      `/api/account-audit${query ? `?${query}` : ""}`
    )
      .then((payload) => {
        setKeepDays(Number(payload.keep_days) || 7);
        setRows(Array.isArray(payload.events) ? payload.events : []);
        setHasMore(Boolean(payload.has_more));
        setCursor(next);
        setBack(history);
      })
      .catch(() => setError("Activity log is unavailable."))
      .finally(() => setLoading(false));
  }, [filterQuery]);

  async function exportCsv() {
    setExporting(true);
    setError("");
    try {
      const collected: AuditRow[] = [];
      let next: Cursor | null = null;
      for (let page = 0; page < 40; page += 1) {
        const query = filterQuery(next);
        const payload = await apiGet<{ events?: AuditRow[]; has_more?: boolean }>(
          `/api/account-audit${query ? `?${query}` : ""}`
        );
        const events = Array.isArray(payload.events) ? payload.events : [];
        collected.push(...events);
        const last = events[events.length - 1];
        if (!payload.has_more || !last?.at || !last.id) break;
        next = { at: last.at, id: last.id };
      }
      const blob = new Blob([`\uFEFF${rowsToCsv(collected, timeZone)}`], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "account-activity.csv";
      link.click();
      window.setTimeout(() => URL.revokeObjectURL(url), 1500);
    } catch {
      setError("CSV export is unavailable.");
    } finally {
      setExporting(false);
    }
  }

  useEffect(() => {
    load(null, []);
  }, [load]);

  function older() {
    const last = rows[rows.length - 1];
    if (!last?.at || !last.id) return;
    load({ at: last.at, id: last.id }, [...back, cursor]);
  }

  function newer() {
    const history = back.slice(0, -1);
    load(back[back.length - 1] ?? null, history);
  }

  return (
    <section className="users-admin-panel">
      <p className="users-admin-hint">
        Sign-in, lockout, logout, and account changes from the last {keepDays} days. Each page shows 50 rows. Passwords,
        authenticator codes, and API secrets are not stored here.
      </p>
      <div className="users-admin-fields">
        <label className="users-admin-label">
          <span>User</span>
          <input className="users-admin-input" value={username} onChange={(e) => setUsername(e.target.value)} placeholder="Exact username" />
        </label>
        <label className="users-admin-label">
          <span>Event</span>
          <select className="users-admin-input" value={action} onChange={(e) => setAction(e.target.value)}>
            <option value="">All events</option>
            {Object.entries(ACTION_LABEL).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </label>
        <label className="users-admin-label">
          <span>Failed only</span>
          <select className="users-admin-input" value={failedOnly ? "1" : "0"} onChange={(e) => setFailedOnly(e.target.value === "1")}>
            <option value="0">All results</option>
            <option value="1">Failed only</option>
          </select>
        </label>
        <div className="account-activity-export">
          <button type="button" className="users-admin-btn-ghost" disabled={exporting} onClick={() => void exportCsv()}>
            {exporting ? "Exporting…" : "Export CSV"}
          </button>
        </div>
      </div>
      {error ? <p className="users-admin-empty">{error}</p> : null}
      <div className="users-admin-table-wrap account-activity-table">
        <table className="users-admin-table">
          <thead>
            <tr>
              <th>When</th>
              <th>User</th>
              <th>Event</th>
              <th>Detail</th>
              <th>Address</th>
            </tr>
          </thead>
          <tbody>
            {!error && !loading && rows.length === 0 ? (
              <tr>
                <td colSpan={5}>No account activity in this page.</td>
              </tr>
            ) : null}
            {rows.map((row) => (
              <tr key={`${row.id || ""}-${row.at || ""}-${row.username || ""}`}>
                <td className="small">{whenLabel(String(row.at || ""), timeZone)}</td>
                <td>
                  <code className="users-admin-mono">{row.username || "—"}</code>
                </td>
                <td>{ACTION_LABEL[String(row.action || "")] || row.action || "—"}</td>
                <td className="small">{row.detail || "—"}</td>
                <td className="small">{row.client_ip || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="users-admin-actions account-activity-pager">
        <button type="button" className="users-admin-btn-ghost" disabled={loading || back.length === 0} onClick={newer}>
          Newer
        </button>
        <button type="button" className="users-admin-btn-ghost" disabled={loading || !hasMore} onClick={older}>
          Older
        </button>
      </div>
    </section>
  );
}

export function AccountActivityPage({ route }: { route: AppRoute; page: PageData }) {
  return (
    <div className="users-admin-page">
      <header className="users-admin-header">
        <div className="users-admin-kicker">{route.section}</div>
        <h2 className="users-admin-title">Account Activity</h2>
      </header>
      <AccountActivity />
    </div>
  );
}
